import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans } from "react-i18next";

const CustomCallToActionPanel = ({ onPause = () => {}, onResume = () => {} }) => {
  const navigate = useNavigate();
  const { id } = useParams(); // Obtener el ID del capítulo desde la URL

  const handleOpinionClick = (e) => {
    // Prevenir la propagación del evento para evitar que se active la pausa del swiper
    e.stopPropagation();
    e.preventDefault();
    navigate('/opiniones');
  };

  // Determinar qué imagen usar basándose en el capítulo
  const getChapterImageNumber = () => {
    if (id) {
      // Extraer el número del capítulo (ej: "capitulo-1" -> "1")
      const chapterMatch = id.match(/\d+/);
      if (chapterMatch) {
        const chapterNum = parseInt(chapterMatch[0]);
        // Asegurar que esté en el rango 1-9
        const finalNumber = Math.min(Math.max(chapterNum, 1), 9);
        // Debug desactivado
        return finalNumber;
      }
    }
    
    // Fallback: intentar extraer desde window.location.pathname
    const pathname = window.location.pathname;
    const pathMatch = pathname.match(/capitulo[/-](\d+)/);
    if (pathMatch) {
      const chapterNum = parseInt(pathMatch[1]);
      const finalNumber = Math.min(Math.max(chapterNum, 1), 9);
      // Debug desactivado
      return finalNumber;
    }
    
    // Debug desactivado
    return 1; // Por defecto usar la imagen 1
  };

  const imageNumber = getChapterImageNumber();
  const backgroundImage = `/images/call-to-action/enlace${imageNumber}.webp`;

  // Componente de botón circular personalizado para la esfera
  const CircularSphereButton = ({ onClick, children, hoverText }) => {
    const [isHovered, setIsHovered] = React.useState(false);

    return (
      <div
        className="relative overflow-hidden rounded-full cursor-pointer transform transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl bg-gradient-to-br from-yellow-400 via-orange-400 to-yellow-500 hover:from-yellow-300 hover:via-orange-300 hover:to-yellow-400 w-full h-full"
        style={{ pointerEvents: "auto" }}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Efecto de brillo interno */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white to-transparent opacity-30 rounded-full"></div>
        
        {/* Brillo superior estilo cómic */}
        <div className="absolute top-3 left-1/4 right-1/4 h-3 bg-white rounded-full opacity-60"></div>
        
        {/* Borde estilo cómic */}
        <div className="absolute inset-0 rounded-full border-2 border-orange-600 opacity-80"></div>
        
        {/* Contenido del botón */}
        <div className="relative z-10 flex items-center justify-center h-full text-white font-bold text-6xl text-center px-2 text-shadow-lg">
          <span className="leading-tight">
            {isHovered ? hoverText : children}
          </span>
        </div>
        
        {/* Efectos de partículas mágicas */}
        <div className="absolute -top-2 -right-2 w-3 h-3 bg-yellow-200 rounded-full opacity-80 animate-ping"></div>
        <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-orange-300 rounded-full opacity-70 animate-pulse"></div>
        <div className="absolute top-2 right-5 w-1 h-1 bg-white rounded-full opacity-90 animate-bounce"></div>
        
        {/* Efecto de hover adicional */}
        <div className={`absolute inset-0 bg-white rounded-full transition-opacity duration-300 ${isHovered ? 'opacity-15' : 'opacity-0'}`}></div>
      </div>
    );
  };

  // Coordenadas originales de la esfera dentro del boceto (1080x1920)
  const ORIGINAL_SPHERE = { cx: 540, cy: 1470, d: 500 };

  return (
    <div className="w-full flex justify-center items-center">
      <svg
        viewBox="0 0 1080 1920"
        className="w-full max-w-[600px] h-auto relative z-10"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Imagen de fondo */}
        <image href={backgroundImage} x="0" y="0" width="1080" height="1920" />

        {/* Efectos de fondo (rayos) eliminados para que las líneas globales se apliquen detrás */}

        {/* Overlay invisible para pausa autoplay */}
        <rect
          x="0"
          y="0"
          width="1080"
          height="1920"
          fill="transparent"
          onMouseDown={onPause}
          onMouseUp={onResume}
          onTouchStart={onPause}
          onTouchEnd={onResume}
        />

        {/* Botón dentro de foreignObject */}
        <foreignObject
          x={ORIGINAL_SPHERE.cx - ORIGINAL_SPHERE.d / 2}
          y={ORIGINAL_SPHERE.cy - ORIGINAL_SPHERE.d / 2}
          width={ORIGINAL_SPHERE.d}
          height={ORIGINAL_SPHERE.d}
        >
          <div className="w-full h-full flex items-center justify-center">
            <CircularSphereButton
              onClick={handleOpinionClick}
              hoverText={<Trans i18nKey="callToAction.primaryButtonHover">¡Danos tu opinión!</Trans>}
            >
              <Trans i18nKey="callToAction.primaryButton">¡Valorar Capítulo!</Trans>
            </CircularSphereButton>
          </div>
        </foreignObject>
      </svg>
    </div>
  );
};

export default CustomCallToActionPanel; 